import { useFortress } from "@foundation/fortress";
import { FortressOptions } from "@foundation/fortress/lib/types";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { useIntl } from "react-intl";
import { useCallback, useMemo, useState } from "react";

const fortressOptions: FortressOptions = {
  articleInfo: {
    contentId: "watchlist",
    metadata: {
      isMetered: true,
    },
    resourceType: "watchlist",
    resourceSubType: "watchlist_hard_wall",
    type: "WATCHLIST",
  },
  paywallData: {
    type: "additive" as const,
  },
};

export function usePaywallAction(): {
  shouldWallAction: boolean;
  blockUserIfWalledActionIsAttempted: () => void;
} {
  const { session } = useRegUIClient();
  const { locale } = useIntl();

  const isBBGSubscriber = Boolean(session?.isBBGSubscriber);
  const { paywallStatus, checkPaywallState, fortressInViewState } =
    useFortress();

  const [hasCheckPaywallStatusBeenCalled, setHasCheckPaywallStatusBeenCalled] =
    useState<boolean>(false);

  const shouldWallAction = useMemo(() => {
    return !isBBGSubscriber;
  }, [isBBGSubscriber]);

  const fortressOptionsWithLocale = useMemo(() => {
    return {
      ...fortressOptions,
      ...(fortressOptions.articleInfo
        ? {
            articleInfo: {
              ...fortressOptions.articleInfo,
              locale,
            },
          }
        : undefined),
    };
  }, [locale]);

  const blockUserIfWalledActionIsAttempted = useCallback(() => {
    /*
      Normally, inView is passed as undefined into fortress client provider. If it is passed as undefined or true,
      then the paywall will be rendered as soon as it is determined that the user is blocked. Since the watchlist page wants
      to block the user as soon as an action is made rather than right away, inView is initially false.

      When inView is false, the fortress script is loaded, but the paywall will never be rendered if the user should should be blocked.
      In other words, paywallStatus will be "pending". This is the scenario we are targetting. If inView is false and paywallStatus is pending,
      that means fortress has or is about to finish loading, but a request has not been made to render the paywall if the user is blocked.
      Calling checkPaywallState() with the second argument set to true will then block the user if fortress determines that the user should be blocked.
    */
    if (
      !hasCheckPaywallStatusBeenCalled &&
      fortressInViewState === false &&
      paywallStatus === "pending"
    ) {
      setHasCheckPaywallStatusBeenCalled(true);
      checkPaywallState(fortressOptionsWithLocale, true);

      return;
    }
  }, [
    hasCheckPaywallStatusBeenCalled,
    fortressInViewState,
    paywallStatus,
    checkPaywallState,
    fortressOptionsWithLocale,
  ]);

  return { shouldWallAction, blockUserIfWalledActionIsAttempted };
}
